/**
 * Pályaépítő: `LevelDef` → Three.js geometria + kollíziós szegmensek.
 *
 * Az algoritmus lényege: minden szoba minden éle alapból tömör fal. A portálok
 * (átjárók) 1D intervallumokat vágnak ki ezekből az élekből; a maradék
 * rész-szakaszokból lesz a tömör falgeometria, a kivágott részből pedig
 * az átjáró — annak alsó küszöbével (lower) és felső szemöldökével (upper),
 * pontosan úgy, ahogy a Doom kezelte a lower/upper textúrákat.
 *
 * Gazda: C
 */

import * as THREE from 'three'
import type { LevelDef, RoomDef, PortalDef, PropDef, DoorDef, LiftDef } from './level'
import type { Vec2 } from '../core/math'
import { polygonArea, pointInPolygon } from '../core/math'
import type { ImpactKind } from '../core/contracts'
import { getTexture } from '../assets/textures'

/** Hány világegység egy textúra-ismétlődés. */
export const TEX_SCALE = 3.6

/** Az él-illesztés tűrése: ennél közelebbi pontot tekintünk az élen lévőnek. */
const EDGE_EPS = 0.03

// ---------------------------------------------------------------------------
// Futásidejű típusok
// ---------------------------------------------------------------------------

export interface DoorRuntime {
  def: DoorDef
  portalId: number
  /** 0 = csukva, 1 = teljesen nyitva. */
  open: number
  state: 'closed' | 'opening' | 'open' | 'closing'
  /** Hátralévő nyitva-tartás másodpercben. */
  waitLeft: number
  mesh: THREE.Mesh
  /** A lap alsó éle csukott állapotban. */
  baseY: number
  /** Mennyit emelkedik teljesen nyitva. */
  travel: number
  /** Blokkolva van-e (áll valaki alatta) — ilyenkor nem csukódik rá. */
  blocked: boolean
}

export interface PortalRuntime {
  def: PortalDef
  id: number
  rooms: [number, number]
  /** A nyílás alsó és felső pereme (statikus). */
  bottom: number
  top: number
  door: DoorRuntime | null
}

export interface LiftRuntime {
  def: LiftDef
  roomId: number
  /** Aktuális padlómagasság. */
  y: number
  target: number
  state: 'idle' | 'moving' | 'waiting'
  waitLeft: number
  mesh: THREE.Mesh
}

export interface RoomRuntime {
  def: RoomDef
  /** CCW-re normalizált poligon. A `def.poly` érintetlen marad. */
  poly: Vec2[]
  id: number
  /** Aktuális padlómagasság (liftnél változik). */
  floorY: number
  ceilY: number
  lift: LiftRuntime | null
  aabb: { minX: number; minZ: number; maxX: number; maxZ: number }
  /** Titkos szobánál: felfedezték-e már. */
  discovered: boolean
  /** A szobát határoló szegmensek indexei. */
  segments: number[]
}

/**
 * Kollíziós szakasz az XZ-síkon. A normális a `room` felé mutat (befelé).
 * Tömör falnál `portal === -1`, és a bottom/top a teljes teret lefedi.
 */
export interface WallSeg {
  ax: number
  az: number
  bx: number
  bz: number
  /** Egységnyi normális, a `room` belseje felé. */
  nx: number
  nz: number
  /** Hossz. */
  len: number
  room: number
  /** Portál-index, vagy -1 ha tömör. */
  portal: number
  /** Prop-index, vagy -1. */
  prop: number
  bottom: number
  top: number
  impact: ImpactKind
}

export interface BuiltLevel {
  def: LevelDef
  group: THREE.Group
  rooms: RoomRuntime[]
  portals: PortalRuntime[]
  doors: DoorRuntime[]
  lifts: LiftRuntime[]
  segments: WallSeg[]
  props: PropDef[]
  /** Térbeli index: cellakulcs → szegmens-indexek. */
  grid: Map<number, number[]>
  cellSize: number
  bounds: { minX: number; minZ: number; maxX: number; maxZ: number }
  dispose(): void
}

// ---------------------------------------------------------------------------
// Geometria-akkumulátor anyagonként
// ---------------------------------------------------------------------------

class MeshBuilder {
  pos: number[] = []
  norm: number[] = []
  uv: number[] = []
  col: number[] = []

  quad(
    p0: THREE.Vector3,
    p1: THREE.Vector3,
    p2: THREE.Vector3,
    p3: THREE.Vector3,
    uvs: [number, number][],
    n: THREE.Vector3,
    cols: number[],
  ): void {
    const pts = [p0, p1, p2, p3]
    const order = [0, 1, 2, 0, 2, 3]
    for (const i of order) {
      const p = pts[i]
      this.pos.push(p.x, p.y, p.z)
      this.norm.push(n.x, n.y, n.z)
      this.uv.push(uvs[i][0], uvs[i][1])
      const c = cols[i]
      this.col.push(c, c, c)
    }
  }

  tri(
    p0: THREE.Vector3,
    p1: THREE.Vector3,
    p2: THREE.Vector3,
    uvs: [number, number][],
    n: THREE.Vector3,
    cols: number[],
  ): void {
    const pts = [p0, p1, p2]
    for (let i = 0; i < 3; i++) {
      const p = pts[i]
      this.pos.push(p.x, p.y, p.z)
      this.norm.push(n.x, n.y, n.z)
      this.uv.push(uvs[i][0], uvs[i][1])
      const c = cols[i]
      this.col.push(c, c, c)
    }
  }

  get empty(): boolean {
    return this.pos.length === 0
  }

  toGeometry(): THREE.BufferGeometry {
    const g = new THREE.BufferGeometry()
    g.setAttribute('position', new THREE.Float32BufferAttribute(this.pos, 3))
    g.setAttribute('normal', new THREE.Float32BufferAttribute(this.norm, 3))
    g.setAttribute('uv', new THREE.Float32BufferAttribute(this.uv, 2))
    g.setAttribute('color', new THREE.Float32BufferAttribute(this.col, 3))
    g.computeBoundingSphere()
    return g
  }
}

const materialCache = new Map<string, THREE.MeshLambertMaterial>()

export function getMaterial(texId: string): THREE.MeshLambertMaterial {
  const hit = materialCache.get(texId)
  if (hit) return hit
  const tex = getTexture(texId)
  const mat = new THREE.MeshLambertMaterial({
    map: tex.map,
    normalMap: tex.normalMap,
    normalScale: new THREE.Vector2(0.8, 0.8),
    vertexColors: true,
  })
  if (tex.emissiveMap) {
    mat.emissiveMap = tex.emissiveMap
    mat.emissive = new THREE.Color(0xffffff)
    mat.emissiveIntensity = 1
  }
  materialCache.set(texId, mat)
  return mat
}

// ---------------------------------------------------------------------------
// Segédek
// ---------------------------------------------------------------------------

/**
 * Függőleges fényárnyalat: a padló és a mennyezet közelében sötétebb.
 * Ez hamisított ambient occlusion — enélkül a falak laposak és „papírszerűek".
 */
function verticalShade(y: number, floorY: number, ceilY: number): number {
  const span = Math.max(0.001, ceilY - floorY)
  const t = (y - floorY) / span
  return 1 - 0.2 * Math.exp(-t * 5) - 0.1 * Math.exp(-(1 - t) * 5)
}

/**
 * A szoba 0..1 fényszintjét vertex-színné képezi.
 *
 * FONTOS: nem közvetlenül szorzunk vele. A vertex szín a textúra albedóját
 * szorozza, az meg már eleve sötét (a paletta szándékosan komor) — ha a
 * 0..1 fényszintet nyersen ráadnánk, minden feketébe fulladna. Ezért a
 * fényszint egy 0.62–1.35 közötti szorzóra képződik: a sötét szoba
 * visszafogja, a világos kissé fel is emeli az alapszínt.
 */
function bakedLight(light: number): number {
  return 0.62 + clampUnit(light) * 0.73
}

function clampUnit(v: number): number {
  return v < 0 ? 0 : v > 1 ? 1 : v
}

/**
 * Szakaszonkénti apró fényeltérés a pozícióból származtatva.
 *
 * A csempézett textúra szabályos ismétlődése az, ami elárulja, hogy a
 * felület generált. Egy ±7%-os, falszakaszonként állandó árnyalatkülönbség
 * megtöri ezt anélkül, hogy foltossá tenné a falat. Determinisztikus:
 * ugyanaz a fal minden futásban ugyanúgy néz ki.
 */
function segmentTint(ax: number, az: number): number {
  const h = Math.sin(ax * 12.9898 + az * 78.233) * 43758.5453
  return 1 + ((h - Math.floor(h)) - 0.5) * 0.14
}

/** A szoba geometriai padlómagassága (liftnél a legalsó állás, hogy ne legyen rés). */
function geomFloorY(room: RoomDef): number {
  return room.lift ? Math.min(room.lift.lowY, room.lift.highY) : room.floorY
}

/** A textúra anyagjellege a becsapódás-effekthez. */
function impactOf(texId: string): ImpactKind {
  if (texId === 'techPanel' || texId === 'metalPlate' || texId === 'metalRibbed' || texId === 'grate' || texId === 'doorTech') {
    return 'metal'
  }
  if (texId === 'flesh') return 'flesh'
  if (texId === 'nukage' || texId === 'lightPanel') return 'energy'
  return 'concrete'
}

interface EdgeHit {
  t0: number
  t1: number
  portal: number
}

/** Pont rajta van-e az [a,b] szakaszon? Ha igen, visszaadja a paramétert. */
function paramOnEdge(p: Vec2, a: Vec2, b: Vec2): number | null {
  const dx = b.x - a.x
  const dz = b.z - a.z
  const lenSq = dx * dx + dz * dz
  if (lenSq < 1e-9) return null
  const t = ((p.x - a.x) * dx + (p.z - a.z) * dz) / lenSq
  if (t < -0.002 || t > 1.002) return null
  const cx = a.x + dx * t
  const cz = a.z + dz * t
  const perp = Math.hypot(p.x - cx, p.z - cz)
  if (perp > EDGE_EPS) return null
  return Math.max(0, Math.min(1, t))
}

// ---------------------------------------------------------------------------
// Fő építő
// ---------------------------------------------------------------------------

export function buildLevel(def: LevelDef): BuiltLevel {
  const group = new THREE.Group()
  group.name = `level:${def.id}`

  const builders = new Map<string, MeshBuilder>()
  const builderFor = (tex: string): MeshBuilder => {
    let b = builders.get(tex)
    if (!b) {
      b = new MeshBuilder()
      builders.set(tex, b)
    }
    return b
  }

  // --- Szobák futásidejű állapota -----------------------------------------
  const rooms: RoomRuntime[] = []
  const roomById = new Map<number, RoomRuntime>()
  for (const r of def.rooms) {
    // A poligont normalizáljuk CCW-re, hogy a normális-számítás egységes
    // legyen — de a TARTALMI adatot nem írjuk felül. A build függvény
    // származtat, nem mutál. (X talált rá — `09-X-revizio.md` F5.)
    const poly = polygonArea(r.poly) < 0 ? [...r.poly].reverse() : r.poly

    let minX = Infinity
    let minZ = Infinity
    let maxX = -Infinity
    let maxZ = -Infinity
    for (const p of poly) {
      minX = Math.min(minX, p.x)
      maxX = Math.max(maxX, p.x)
      minZ = Math.min(minZ, p.z)
      maxZ = Math.max(maxZ, p.z)
    }
    const rr: RoomRuntime = {
      def: r,
      poly,
      id: r.id,
      floorY: r.lift ? (r.lift.startAt === 'high' ? r.lift.highY : r.lift.lowY) : r.floorY,
      ceilY: r.ceilY,
      lift: null,
      aabb: { minX, minZ, maxX, maxZ },
      discovered: false,
      segments: [],
    }
    rooms.push(rr)
    roomById.set(r.id, rr)
  }

  // --- Portálok ------------------------------------------------------------
  const portals: PortalRuntime[] = []
  const doors: DoorRuntime[] = []
  for (const p of def.portals) {
    const ra = roomById.get(p.rooms[0])
    const rb = roomById.get(p.rooms[1])
    if (!ra || !rb) {
      throw new Error(`Portál ${p.id}: ismeretlen szoba (${p.rooms[0]}, ${p.rooms[1]})`)
    }
    const bottom = p.bottom ?? Math.max(geomFloorY(ra.def), geomFloorY(rb.def))
    const top = p.top ?? Math.min(ra.ceilY, rb.ceilY)
    if (top <= bottom) {
      throw new Error(`Portál ${p.id}: a nyílás magassága nem pozitív (${bottom} → ${top})`)
    }
    portals.push({ def: p, id: p.id, rooms: p.rooms, bottom, top, door: null })
  }

  // --- Falak: élenkénti intervallum-kivágás ---------------------------------
  const segments: WallSeg[] = []
  const tmpA = new THREE.Vector3()
  const tmpB = new THREE.Vector3()

  for (const rr of rooms) {
    const room = rr.def
    const poly = rr.poly
    const fY = geomFloorY(room)
    const cY = room.ceilY

    for (let i = 0; i < poly.length; i++) {
      const a = poly[i]
      const b = poly[(i + 1) % poly.length]
      const dx = b.x - a.x
      const dz = b.z - a.z
      const len = Math.hypot(dx, dz)
      if (len < 1e-6) continue
      const ux = dx / len
      const uz = dz / len

      // Befelé mutató normális: két jelölt közül az, amelyik a poligonba mutat.
      let nx = -uz
      let nz = ux
      if (!pointInPolygon((a.x + b.x) / 2 + nx * 0.05, (a.z + b.z) / 2 + nz * 0.05, poly)) {
        nx = -nx
        nz = -nz
      }

      // Melyik portálok fekszenek ezen az élen?
      const hits: EdgeHit[] = []
      for (let pi = 0; pi < portals.length; pi++) {
        const pr = portals[pi]
        if (pr.rooms[0] !== room.id && pr.rooms[1] !== room.id) continue
        const t0 = paramOnEdge(pr.def.p0, a, b)
        const t1 = paramOnEdge(pr.def.p1, a, b)
        if (t0 === null || t1 === null) continue
        hits.push({ t0: Math.min(t0, t1), t1: Math.max(t0, t1), portal: pi })
      }
      hits.sort((x, y) => x.t0 - y.t0)

      // Átfedő portálok: a tömör fal kivágása az uniójukból történne, de
      // mindkettőhöz külön küszöb/szemöldök geometria és külön kollíziós
      // szegmens készülne — Z-harc és sorrendfüggő átjáró-viselkedés lenne
      // belőle. Ez tartalmi hiba, ezért hangosan elbukunk, nem csendben
      // rosszul építünk. (X talált rá — `09-X-revizio.md` F4.)
      for (let h = 1; h < hits.length; h++) {
        if (hits[h].t0 < hits[h - 1].t1 - 1e-6) {
          throw new Error(
            `Átfedő portálok a(z) ${room.id}. szoba ${i}. élén: ` +
              `#${portals[hits[h - 1].portal].id} és #${portals[hits[h].portal].id}`,
          )
        }
      }

      // 1) A kivágások közti tömör darabok
      let cursor = 0
      const emitSolid = (s: number, e: number) => {
        if (e - s < 1e-4) return
        const p0x = a.x + dx * s
        const p0z = a.z + dz * s
        const p1x = a.x + dx * e
        const p1z = a.z + dz * e
        pushWallQuad(builderFor(room.tex.wall), p0x, p0z, p1x, p1z, fY, cY, nx, nz, ux, uz, room.light, fY, cY)
        segments.push({
          ax: p0x,
          az: p0z,
          bx: p1x,
          bz: p1z,
          nx,
          nz,
          len: len * (e - s),
          room: room.id,
          portal: -1,
          prop: -1,
          bottom: -1e9,
          top: 1e9,
          impact: impactOf(room.tex.wall),
        })
      }

      for (const h of hits) {
        emitSolid(cursor, h.t0)
        cursor = Math.max(cursor, h.t1)
      }
      emitSolid(cursor, 1)

      // 2) A portál-darabok: küszöb (lower) és szemöldök (upper) ehhez a szobához
      for (const h of hits) {
        const pr = portals[h.portal]
        const p0x = a.x + dx * h.t0
        const p0z = a.z + dz * h.t0
        const p1x = a.x + dx * h.t1
        const p1z = a.z + dz * h.t1

        if (pr.bottom > fY + 1e-4) {
          pushWallQuad(builderFor(room.tex.wall), p0x, p0z, p1x, p1z, fY, pr.bottom, nx, nz, ux, uz, room.light, fY, cY)
        }
        if (pr.top < cY - 1e-4) {
          pushWallQuad(builderFor(room.tex.wall), p0x, p0z, p1x, p1z, pr.top, cY, nx, nz, ux, uz, room.light, fY, cY)
        }

        segments.push({
          ax: p0x,
          az: p0z,
          bx: p1x,
          bz: p1z,
          nx,
          nz,
          len: len * (h.t1 - h.t0),
          room: room.id,
          portal: h.portal,
          prop: -1,
          bottom: pr.bottom,
          top: pr.top,
          impact: impactOf(room.tex.wall),
        })
      }
    }
  }

  // --- Padló és mennyezet ---------------------------------------------------
  const liftMeshes: LiftRuntime[] = []
  for (const rr of rooms) {
    const room = rr.def
    const tris = triangulate(rr.poly)

    if (room.lift) {
      // A lift padlója külön, mozgatható mesh.
      //
      // FONTOS, hogy VASTAG legyen. Egyetlen, felfelé néző lapból a felső
      // állásban lévő lift alulról nézve láthatatlan (a hátlapvágás eldobja),
      // és a játékos egy lyukat lát a mennyezetben. Ezért a lap alá kerül egy
      // lefelé néző lap és körben oldalfalak: így valódi, zömök táblának
      // látszik, ahogy egy teherlifttől elvárható.
      const LIFT_THICK = 0.35
      const mb = new MeshBuilder()
      emitHorizontal(mb, rr.poly, tris, 0, true, room.light)
      emitHorizontal(mb, rr.poly, tris, -LIFT_THICK, false, room.light * 0.55)

      for (let i = 0; i < rr.poly.length; i++) {
        const a = rr.poly[i]
        const b = rr.poly[(i + 1) % rr.poly.length]
        const dx = b.x - a.x
        const dz = b.z - a.z
        const len = Math.hypot(dx, dz)
        if (len < 1e-6) continue
        const ux = dx / len
        const uz = dz / len
        let nx = -uz
        let nz = ux
        if (pointInPolygon((a.x + b.x) / 2 + nx * 0.05, (a.z + b.z) / 2 + nz * 0.05, rr.poly)) {
          nx = -nx
          nz = -nz
        }
        pushWallQuad(mb, a.x, a.z, b.x, b.z, -LIFT_THICK, 0, nx, nz, ux, uz, room.light * 0.8, -LIFT_THICK, 0)
      }
      const mesh = new THREE.Mesh(mb.toGeometry(), getMaterial(room.tex.floor))
      mesh.position.y = rr.floorY
      mesh.name = `lift:${room.id}`
      group.add(mesh)
      const lr: LiftRuntime = {
        def: room.lift,
        roomId: room.id,
        y: rr.floorY,
        target: rr.floorY,
        state: 'idle',
        waitLeft: 0,
        mesh,
      }
      rr.lift = lr
      liftMeshes.push(lr)
    } else {
      emitHorizontal(builderFor(room.tex.floor), rr.poly, tris, room.floorY, true, room.light)
    }

    if (!room.sky) {
      emitHorizontal(builderFor(room.tex.ceil), rr.poly, tris, room.ceilY, false, room.light * 0.82)
    }
  }

  // --- Propok ---------------------------------------------------------------
  for (const prop of def.props) {
    emitProp(builderFor(prop.tex), prop, segments, def)
  }

  // --- Ajtólapok ------------------------------------------------------------
  for (const pr of portals) {
    const d = pr.def.door
    if (!d) continue
    const mesh = buildDoorPanel(pr, d)
    group.add(mesh)
    const dr: DoorRuntime = {
      def: d,
      portalId: pr.id,
      open: 0,
      state: 'closed',
      waitLeft: 0,
      mesh,
      baseY: pr.bottom,
      travel: pr.top - pr.bottom,
      blocked: false,
    }
    pr.door = dr
    doors.push(dr)
  }

  // --- Statikus meshek összefűzése -----------------------------------------
  for (const [texId, mb] of builders) {
    if (mb.empty) continue
    const mesh = new THREE.Mesh(mb.toGeometry(), getMaterial(texId))
    mesh.name = `static:${texId}`
    mesh.matrixAutoUpdate = false
    mesh.updateMatrix()
    group.add(mesh)
  }

  // --- Szegmensek szoba szerinti indexelése + térbeli rács ------------------
  for (let i = 0; i < segments.length; i++) {
    const rr = roomById.get(segments[i].room)
    if (rr) rr.segments.push(i)
  }

  let minX = Infinity
  let minZ = Infinity
  let maxX = -Infinity
  let maxZ = -Infinity
  for (const rr of rooms) {
    minX = Math.min(minX, rr.aabb.minX)
    minZ = Math.min(minZ, rr.aabb.minZ)
    maxX = Math.max(maxX, rr.aabb.maxX)
    maxZ = Math.max(maxZ, rr.aabb.maxZ)
  }

  const cellSize = 4
  const grid = buildGrid(segments, cellSize)

  void tmpA
  void tmpB

  return {
    def,
    group,
    rooms,
    portals,
    doors,
    lifts: liftMeshes,
    segments,
    props: def.props,
    grid,
    cellSize,
    bounds: { minX, minZ, maxX, maxZ },
    dispose() {
      group.traverse((o) => {
        if (o instanceof THREE.Mesh) o.geometry.dispose()
      })
      group.clear()
    },
  }
}

// ---------------------------------------------------------------------------
// Részfeladatok
// ---------------------------------------------------------------------------

function pushWallQuad(
  mb: MeshBuilder,
  ax: number,
  az: number,
  bx: number,
  bz: number,
  yBottom: number,
  yTop: number,
  nx: number,
  nz: number,
  ux: number,
  uz: number,
  light: number,
  shadeFloor: number,
  shadeCeil: number,
): void {
  if (yTop - yBottom < 1e-4) return

  // A quad tájolása úgy, hogy a lapnormális (nx, nz) legyen.
  // A (p0=a_bot, p1=b_bot, p2=b_top, p3=a_top) sorrend normálisa (-dz, 0, dx).
  const wantFlip = -uz * nx + ux * nz < 0
  const p0 = new THREE.Vector3(ax, yBottom, az)
  const p1 = new THREE.Vector3(bx, yBottom, bz)
  const p2 = new THREE.Vector3(bx, yTop, bz)
  const p3 = new THREE.Vector3(ax, yTop, az)

  // UV: a fal irányára vetített világkoordináta → a szomszédos rész-szakaszok
  // textúrája folytonos marad, akárhol vágta ketté a portál.
  const uA = (ax * ux + az * uz) / TEX_SCALE
  const uB = (bx * ux + bz * uz) / TEX_SCALE
  const vB = yBottom / TEX_SCALE
  const vT = yTop / TEX_SCALE

  const baked = bakedLight(light) * segmentTint(ax, az)
  const cBot = baked * verticalShade(yBottom, shadeFloor, shadeCeil)
  const cTop = baked * verticalShade(yTop, shadeFloor, shadeCeil)

  const n = new THREE.Vector3(nx, 0, nz)
  if (!wantFlip) {
    mb.quad(p0, p1, p2, p3, [[uA, vB], [uB, vB], [uB, vT], [uA, vT]], n, [cBot, cBot, cTop, cTop])
  } else {
    mb.quad(p1, p0, p3, p2, [[uB, vB], [uA, vB], [uA, vT], [uB, vT]], n, [cBot, cBot, cTop, cTop])
  }
}

/** Poligon háromszögelése (earcut a Three-ből). Indexhármasokat ad vissza. */
function triangulate(poly: Vec2[]): number[][] {
  const contour = poly.map((p) => new THREE.Vector2(p.x, p.z))
  const faces = THREE.ShapeUtils.triangulateShape(contour, [])
  return faces
}

/** Vízszintes lap (padló vagy mennyezet) a poligonból. */
function emitHorizontal(
  mb: MeshBuilder,
  poly: Vec2[],
  tris: number[][],
  y: number,
  faceUp: boolean,
  light: number,
): void {
  const n = new THREE.Vector3(0, faceUp ? 1 : -1, 0)
  const baked = bakedLight(light)
  for (const f of tris) {
    const pts = f.map((i) => new THREE.Vector3(poly[i].x, y, poly[i].z))
    const uvs: [number, number][] = f.map((i) => [poly[i].x / TEX_SCALE, poly[i].z / TEX_SCALE])

    // Ellenőrizzük, hogy a háromszög körüljárása a kívánt normálishoz illik-e.
    const e1x = pts[1].x - pts[0].x
    const e1z = pts[1].z - pts[0].z
    const e2x = pts[2].x - pts[0].x
    const e2z = pts[2].z - pts[0].z
    const cross = e1x * e2z - e1z * e2x
    // (x,z) síkon a pozitív kereszt-szorzat lefelé néző lapot ad Y-fel felfelé.
    const needsFlip = faceUp ? cross > 0 : cross < 0

    if (needsFlip) {
      mb.tri(pts[0], pts[2], pts[1], [uvs[0], uvs[2], uvs[1]], n, [baked, baked, baked])
    } else {
      mb.tri(pts[0], pts[1], pts[2], [uvs[0], uvs[1], uvs[2]], n, [baked, baked, baked])
    }
  }
}

/** Prop (doboz vagy henger) geometriája + oldalfalak kollíziós szegmensként. */
function emitProp(mb: MeshBuilder, prop: PropDef, segments: WallSeg[], def: LevelDef): void {
  const corners: Vec2[] = []
  if (prop.kind === 'box') {
    const yaw = prop.yaw ?? 0
    const c = Math.cos(yaw)
    const s = Math.sin(yaw)
    const hw = prop.size.x
    const hd = prop.size.z
    const local: Vec2[] = [
      { x: -hw, z: -hd },
      { x: hw, z: -hd },
      { x: hw, z: hd },
      { x: -hw, z: hd },
    ]
    for (const p of local) {
      corners.push({ x: prop.pos.x + p.x * c - p.z * s, z: prop.pos.z + p.x * s + p.z * c })
    }
  } else {
    const sides = 10
    for (let i = 0; i < sides; i++) {
      const a = (i / sides) * Math.PI * 2 + (prop.yaw ?? 0)
      corners.push({ x: prop.pos.x + Math.cos(a) * prop.size.x, z: prop.pos.z + Math.sin(a) * prop.size.x })
    }
  }

  // A propnál a normális KIFELÉ mutat (a szobába), ezért a poligont kívülről
  // nézzük: a `pointInPolygon` teszt fordítva dönt, mint a szobáknál.
  const light = roomLightAt(def, prop.pos.x, prop.pos.z)

  for (let i = 0; i < corners.length; i++) {
    const a = corners[i]
    const b = corners[(i + 1) % corners.length]
    const dx = b.x - a.x
    const dz = b.z - a.z
    const len = Math.hypot(dx, dz)
    if (len < 1e-6) continue
    const ux = dx / len
    const uz = dz / len
    let nx = -uz
    let nz = ux
    // Kifelé mutasson: ha a normális mentén eltolt pont a poligonon BELÜL van,
    // akkor rossz irányba mutat.
    if (pointInPolygon((a.x + b.x) / 2 + nx * 0.02, (a.z + b.z) / 2 + nz * 0.02, corners)) {
      nx = -nx
      nz = -nz
    }

    pushWallQuad(mb, a.x, a.z, b.x, b.z, prop.bottom, prop.top, nx, nz, ux, uz, light, prop.bottom, prop.top)

    if (prop.solid) {
      segments.push({
        ax: a.x,
        az: a.z,
        bx: b.x,
        bz: b.z,
        nx,
        nz,
        len,
        room: -1,
        portal: -1,
        prop: prop.id,
        bottom: prop.bottom,
        top: prop.top,
        impact: 'metal',
      })
    }
  }

  // Fedlap és alsó lap
  const tris = triangulate(corners)
  emitHorizontal(mb, corners, tris, prop.top, true, light * 1.05)
  emitHorizontal(mb, corners, tris, prop.bottom, false, light * 0.7)
}

function roomLightAt(def: LevelDef, x: number, z: number): number {
  for (const r of def.rooms) {
    if (pointInPolygon(x, z, r.poly)) return r.light
  }
  return 0.5
}

/** Az ajtólap mesh-e: a nyílást kitöltő, kétoldalas tábla. */
function buildDoorPanel(pr: PortalRuntime, d: DoorDef): THREE.Mesh {
  const a = pr.def.p0
  const b = pr.def.p1
  const dx = b.x - a.x
  const dz = b.z - a.z
  const len = Math.hypot(dx, dz)
  const height = pr.top - pr.bottom
  const thickness = 0.22

  const geo = new THREE.BoxGeometry(len, height, thickness)
  // UV-t átskálázzuk, hogy az ajtótextúra egyszer feszüljön ki a lapra.
  const mat = getMaterial(d.tex)
  const mesh = new THREE.Mesh(geo, mat)

  // A BoxGeometry-nek nincs vertex színe — pótoljuk, hogy a vertexColors
  // anyag ne feketére vegye.
  const count = geo.attributes.position.count
  const colors = new Float32Array(count * 3).fill(0.85)
  geo.setAttribute('color', new THREE.BufferAttribute(colors, 3))

  mesh.position.set((a.x + b.x) / 2, pr.bottom + height / 2, (a.z + b.z) / 2)
  mesh.rotation.y = Math.atan2(dx, dz) + Math.PI / 2
  mesh.name = `door:${pr.id}`
  return mesh
}

/** Térbeli rács a szegmensekhez — a kollízió és a raycast gyorsítója. */
function buildGrid(segments: WallSeg[], cellSize: number): Map<number, number[]> {
  const grid = new Map<number, number[]>()
  const put = (cx: number, cz: number, i: number) => {
    const key = cellKey(cx, cz)
    let list = grid.get(key)
    if (!list) {
      list = []
      grid.set(key, list)
    }
    if (list[list.length - 1] !== i) list.push(i)
  }

  for (let i = 0; i < segments.length; i++) {
    const s = segments[i]
    // A szakasz által érintett cellák (konzervatív AABB-vel).
    const x0 = Math.floor(Math.min(s.ax, s.bx) / cellSize)
    const x1 = Math.floor(Math.max(s.ax, s.bx) / cellSize)
    const z0 = Math.floor(Math.min(s.az, s.bz) / cellSize)
    const z1 = Math.floor(Math.max(s.az, s.bz) / cellSize)
    for (let cz = z0; cz <= z1; cz++) {
      for (let cx = x0; cx <= x1; cx++) put(cx, cz, i)
    }
  }
  return grid
}

export function cellKey(cx: number, cz: number): number {
  // 16 bites eltolás: ±32000 cellát bír, bőven elég.
  return ((cx + 32768) << 16) | (cz + 32768)
}
